/**
 * El nombre del comercio que FIRMA los mensajes de una campaña («te extrañamos en <marca>», el asunto y el pie
 * de los mails), a la vista y editable.
 *
 * QA del 08/10: con un comercio sin nombre legible (en local, el `hatsu-demo` de siempre) la campaña quedaba
 * firmada «Tu marca»: los clientes recibían «te extrañamos en Tu marca» y nada en el asistente lo mostraba ni
 * dejaba corregirlo. Ahora se ve con quién salen los mensajes y, si falta el nombre, se pide antes de armarlos
 * (el backend tampoco arma ni envía sin él: SIN_MARCA).
 *
 * Guarda en la campaña (`POST /campaigns/:id/marca`); si los mensajes ya estaban armados, el backend los rearma
 * con el nombre nuevo y devuelve la campaña actualizada (`onGuardada`).
 */
import { useState } from "react";
import { AlertTriangle, Loader2, Store } from "lucide-react";
import { activaApi } from "../api";
import { MARCA_MAX, marcaQueFirma } from "../utils/marcaCampana";

interface Props {
  campaignId: string;
  marca?: string;
  /** la campaña como quedó en el backend (con la marca y, si estaban armados, los mensajes nuevos) */
  onGuardada: (campana: any) => void;
}

export function FirmaComercio({ campaignId, marca, onGuardada }: Props) {
  const actual = marcaQueFirma(marca);
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(actual);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const falta = !actual;
  const limpio = valor.trim();

  const guardar = async () => {
    if (!marcaQueFirma(limpio)) { setError("Escribí el nombre de tu comercio, como lo conocen tus clientes."); return; }
    setGuardando(true); setError("");
    try {
      const r = await activaApi.cambiarMarca(campaignId, limpio);
      // un 422 (vacío, muy largo, la campaña ya salió) vuelve sin lanzar, con el motivo del backend
      if (r?.success === false || !r?.data) { setError(r?.error || "No pudimos guardar el nombre. Probá de nuevo."); return; }
      onGuardada(r.data);
      setEditando(false);
    } catch (e: any) {
      setError(e?.message || "No pudimos guardar el nombre. Probá de nuevo.");
    } finally {
      setGuardando(false);
    }
  };

  if (!falta && !editando) {
    return (
      <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3.5 py-2.5 flex items-center gap-2.5 text-sm">
        <Store size={15} className="text-slate-400 flex-shrink-0" />
        <p className="flex-1 min-w-0 text-slate-600">
          Los mensajes van firmados por <strong className="text-slate-900">«{actual}»</strong>
        </p>
        <button
          type="button"
          onClick={() => { setValor(actual); setEditando(true); setError(""); }}
          className="text-xs font-semibold text-violet-600 hover:text-violet-800 flex-shrink-0"
        >
          Cambiar
        </button>
      </div>
    );
  }

  return (
    <div className={`rounded-xl p-3.5 ${falta ? "border-2 border-amber-300 bg-amber-50" : "border border-slate-200 bg-white"}`}>
      <label htmlFor={`firma-${campaignId}`} className={`text-sm font-bold flex items-center gap-1.5 ${falta ? "text-amber-900" : "text-slate-800"}`}>
        {falta ? <AlertTriangle size={15} className="text-amber-600 flex-shrink-0" /> : <Store size={15} className="text-slate-400 flex-shrink-0" />}
        ¿Cómo se llama tu comercio?
      </label>
      <p className={`text-xs mt-1 leading-relaxed ${falta ? "text-amber-800" : "text-slate-500"}`}>
        Va en los mensajes («te extrañamos en …») y en el asunto de los mails.
        {falta && " Sin el nombre, los mensajes no se pueden armar ni enviar."}
      </p>
      <div className="mt-2.5 flex gap-2 flex-wrap sm:flex-nowrap">
        <input
          id={`firma-${campaignId}`}
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void guardar(); }}
          maxLength={MARCA_MAX}
          placeholder="Por ejemplo: La Fábrica"
          className="flex-1 min-w-0 h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
        />
        <button
          type="button"
          onClick={() => void guardar()}
          disabled={guardando || !limpio}
          className="h-10 px-4 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
        >
          {guardando && <Loader2 size={14} className="animate-spin" />} Guardar
        </button>
        {!falta && (
          <button type="button" onClick={() => { setEditando(false); setError(""); }} className="h-10 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
            Cancelar
          </button>
        )}
      </div>
      {error && <p role="alert" className="text-xs text-red-700 mt-2">{error}</p>}
    </div>
  );
}
